import { useState, useEffect, useId } from 'react'
import { CheckCircle, Copy, RefreshCw, Trash2, ChevronDown, ChevronUp } from 'lucide-react'
import { useWorkspaceStore } from '../stores/workspaceStore'
import { emailDomainApi, EmailDomainConfig, DnsRecord } from '../services/api'
import {
  Alert,
  Badge,
  Button,
  Card,
  CardHeader,
  CardList,
  Checkbox,
  Code,
  CodeBlock,
  ConfirmDialog,
  Field,
  FieldGrid,
  Hint,
  IconButton,
  Inline,
  KeyValue,
  LoadingState,
  SettingRow,
  Stack,
} from '../ds'

// Status badge component
const StatusBadge = ({ status }: { status: string }) => {
  const config = ({
    verified: { tone: 'sage', label: 'Verified' },
    pending: { tone: 'gold', label: 'Pending' },
    failed: { tone: 'danger', label: 'Failed' },
    expired: { tone: 'gold', label: 'Expired' },
  } as const)[status as 'verified' | 'pending' | 'failed' | 'expired'] || { tone: 'neutral' as const, label: status }

  return <Badge tone={config.tone} dot>{config.label}</Badge>
}

// One DNS record: its type, whether it is required, its state, and the two values to copy.
const DnsRecordRow = ({ record }: { record: DnsRecord }) => {
  const [copied, setCopied] = useState<'name' | 'value' | null>(null)

  const handleCopy = (text: string, field: 'name' | 'value') => {
    navigator.clipboard.writeText(text)
    setCopied(field)
    setTimeout(() => setCopied(null), 2000)
  }

  const verified = record.status === 'verified'

  return (
    <Card tone="recessed" padding="sm" data-testid="dns-record">
      <Inline justify="between">
        <Inline>
          <Badge tone={record.type === 'CNAME' ? 'sage' : 'gold'}>{record.type}</Badge>
          {record.required ? <Badge tone="danger">Required</Badge> : <Badge>Recommended</Badge>}
        </Inline>
        <Badge tone={verified ? 'sage' : 'gold'} dot>{verified ? 'Verified' : 'Pending'}</Badge>
      </Inline>
      <SettingRow
        title="Name"
        description={<CodeBlock>{record.name}</CodeBlock>}
        aside={
          <IconButton
            label="Copy name"
            icon={copied === 'name' ? <CheckCircle size={14} /> : <Copy size={14} />}
            onClick={() => handleCopy(record.name, 'name')}
          />
        }
      />
      <SettingRow
        title="Value"
        description={<CodeBlock>{record.value}</CodeBlock>}
        aside={
          <IconButton
            label="Copy value"
            icon={copied === 'value' ? <CheckCircle size={14} /> : <Copy size={14} />}
            onClick={() => handleCopy(record.value, 'value')}
          />
        }
      />
    </Card>
  )
}

// Domain Setup Form (shown when no domain configured)
const DomainSetupForm = ({ onSuccess }: { onSuccess: () => void }) => {
  const { currentWorkspace } = useWorkspaceStore()
  const [formData, setFormData] = useState({
    domain: '',
    useSubdomain: true,
    fromName: currentWorkspace?.name || '',
    fromLocalPart: 'hello'
  })
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!currentWorkspace) return

    setIsSubmitting(true)
    setError(null)

    try {
      const response = await emailDomainApi.initiate(currentWorkspace.id, formData)
      if (response.success) {
        onSuccess()
      } else {
        setError(response.error || 'Failed to initiate domain setup')
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred')
    } finally {
      setIsSubmitting(false)
    }
  }

  const sendingDomain = `${formData.useSubdomain ? 'mail.' : ''}${formData.domain || 'yourdomain.com'}`

  return (
    <form onSubmit={handleSubmit}>
      <Stack>
        <p>
          Configure your email domain to send marketing emails from your own domain.
          This improves deliverability and brand trust.
        </p>

        {error && <Alert tone="danger" live="assertive">{error}</Alert>}

        <Field
          label="Your Domain"
          value={formData.domain}
          onChange={(e) => setFormData({ ...formData, domain: (e.target as HTMLInputElement).value })}
          placeholder="example.com"
          hint="Enter your root domain (e.g., example.com)"
          required
          disabled={isSubmitting}
        />

        <Checkbox
          id="useSubdomain"
          label="Use subdomain (recommended)"
          description={`Emails will be sent from mail.${formData.domain || 'yourdomain.com'} to protect your root domain reputation.`}
          checked={formData.useSubdomain}
          onChange={(e) => setFormData({ ...formData, useSubdomain: e.target.checked })}
          disabled={isSubmitting}
        />

        <FieldGrid>
          <Field
            label="From Name"
            value={formData.fromName}
            onChange={(e) => setFormData({ ...formData, fromName: (e.target as HTMLInputElement).value })}
            placeholder="Your Store Name"
            required
            disabled={isSubmitting}
          />
          <Field
            label="From Address"
            value={formData.fromLocalPart}
            onChange={(e) => setFormData({ ...formData, fromLocalPart: (e.target as HTMLInputElement).value })}
            placeholder="hello"
            suffix={`@${sendingDomain}`}
            disabled={isSubmitting}
          />
        </FieldGrid>

        <Inline>
          <Button
            type="submit"
            disabled={!formData.domain || !formData.fromName}
            busy={isSubmitting}
            busyLabel="Setting up..."
          >
            Setup Domain
          </Button>
        </Inline>
      </Stack>
    </form>
  )
}

// Domain Status View (shown when domain is configured)
const DomainStatusView = ({ config, onRefresh, onRemove }: {
  config: EmailDomainConfig
  onRefresh: () => void
  onRemove: () => void
}) => {
  const { currentWorkspace } = useWorkspaceStore()
  const [isVerifying, setIsVerifying] = useState(false)
  const [isRemoving, setIsRemoving] = useState(false)
  const [showDnsRecords, setShowDnsRecords] = useState(config.verificationStatus !== 'verified')
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null)

  // From address editing
  const [isEditingFrom, setIsEditingFrom] = useState(false)
  const [fromForm, setFromForm] = useState({
    fromName: config.fromName,
    fromLocalPart: config.fromLocalPart,
    replyToAddress: config.replyToAddress || ''
  })
  const [isSavingFrom, setIsSavingFrom] = useState(false)

  const handleVerify = async () => {
    if (!currentWorkspace) return
    setIsVerifying(true)
    setMessage(null)

    try {
      const response = await emailDomainApi.verify(currentWorkspace.id)
      if (response.success) {
        setMessage({ type: 'success', text: `Status: ${response.data?.verificationStatus}` })
        onRefresh()
      } else {
        setMessage({ type: 'error', text: response.error || 'Verification check failed' })
      }
    } catch (err) {
      setMessage({ type: 'error', text: err instanceof Error ? err.message : 'Error checking verification' })
    } finally {
      setIsVerifying(false)
    }
  }

  // Removing is confirmed first (migration.md: disconnects -> ConfirmDialog; was window.confirm).
  const [confirmRemove, setConfirmRemove] = useState(false)
  const dnsId = `dns-${useId().replace(/:/g, '')}`

  const handleRemove = async () => {
    if (!currentWorkspace) return

    setIsRemoving(true)
    try {
      const response = await emailDomainApi.remove(currentWorkspace.id)
      if (response.success) {
        onRemove()
      } else {
        setMessage({ type: 'error', text: response.error || 'Failed to remove domain' })
      }
    } catch (err) {
      setMessage({ type: 'error', text: err instanceof Error ? err.message : 'Error removing domain' })
    } finally {
      setIsRemoving(false)
    }
  }

  const handleSaveFromAddress = async () => {
    if (!currentWorkspace) return
    setIsSavingFrom(true)
    setMessage(null)

    try {
      const response = await emailDomainApi.updateFromAddress(currentWorkspace.id, {
        fromName: fromForm.fromName,
        fromLocalPart: fromForm.fromLocalPart,
        replyToAddress: fromForm.replyToAddress || undefined
      })
      if (response.success) {
        setMessage({ type: 'success', text: 'From address updated!' })
        setIsEditingFrom(false)
        onRefresh()
      } else {
        setMessage({ type: 'error', text: response.error || 'Failed to update' })
      }
    } catch (err) {
      setMessage({ type: 'error', text: err instanceof Error ? err.message : 'Error updating' })
    } finally {
      setIsSavingFrom(false)
    }
  }

  return (
    <Stack>
      {message && (
        <Alert tone={message.type === 'success' ? 'success' : 'danger'} live="polite">{message.text}</Alert>
      )}

      {/* Status Overview */}
      <Card tone="recessed" padding="sm">
        <CardHeader
          title={config.domain}
          titleAs="h4"
          description={<>Emails sent from: <Code>{config.fromAddress}</Code></>}
          aside={<StatusBadge status={config.verificationStatus} />}
        />
        <Stack gap="sm">
          {config.verificationStatus === 'verified' && config.verifiedAt && (
            <Hint>Verified on {new Date(config.verifiedAt).toLocaleDateString()}</Hint>
          )}
          {config.verificationStatus === 'failed' && config.failureReason && (
            <Alert tone="danger">{config.failureReason}</Alert>
          )}
          {config.verificationStatus === 'pending' && (
            <Alert tone="attention">
              Add the DNS records below to your domain, then click "Check Status" to verify.
            </Alert>
          )}
        </Stack>
      </Card>

      {/* From Address Configuration */}
      <Card tone="recessed" padding="sm">
        <CardHeader
          title="From Address"
          titleAs="h4"
          aside={
            !isEditingFrom && config.verificationStatus === 'verified' ? (
              <Button variant="ghost" size="sm" onClick={() => setIsEditingFrom(true)}>Edit</Button>
            ) : undefined
          }
        />

        {isEditingFrom ? (
          <Stack gap="sm">
            <FieldGrid>
              <Field
                label="From Name"
                value={fromForm.fromName}
                onChange={(e) => setFromForm({ ...fromForm, fromName: (e.target as HTMLInputElement).value })}
              />
              <Field
                label="Local Part"
                value={fromForm.fromLocalPart}
                onChange={(e) => setFromForm({ ...fromForm, fromLocalPart: (e.target as HTMLInputElement).value })}
                suffix={`@${config.domain}`}
              />
            </FieldGrid>
            <Field
              label="Reply-To"
              optional
              type="email"
              value={fromForm.replyToAddress}
              onChange={(e) => setFromForm({ ...fromForm, replyToAddress: (e.target as HTMLInputElement).value })}
              placeholder="support@example.com"
            />
            <Inline>
              <Button size="sm" onClick={handleSaveFromAddress} busy={isSavingFrom} busyLabel="Saving...">Save</Button>
              <Button variant="ghost" size="sm" onClick={() => setIsEditingFrom(false)}>Cancel</Button>
            </Inline>
          </Stack>
        ) : (
          <KeyValue
            items={[
              { label: 'Name', value: config.fromName },
              { label: 'Email', value: config.fromAddress },
              ...(config.replyToAddress ? [{ label: 'Reply-To', value: config.replyToAddress }] : []),
            ]}
          />
        )}
      </Card>

      {/* DNS Records Section */}
      <Card tone="recessed" padding="sm">
        <CardHeader
          title="DNS Records"
          titleAs="h4"
          aside={
            <IconButton
              label="DNS Records"
              aria-expanded={showDnsRecords}
              aria-controls={dnsId}
              icon={showDnsRecords ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
              onClick={() => setShowDnsRecords(!showDnsRecords)}
            />
          }
        />
        {showDnsRecords && config.dnsRecords && (
          <CardList id={dnsId}>
            {config.dnsRecords.map((record, idx) => (
              <DnsRecordRow key={idx} record={record} />
            ))}
          </CardList>
        )}
      </Card>

      {/* Actions */}
      <Inline justify="between">
        <Button icon={<RefreshCw size={16} />} onClick={handleVerify} busy={isVerifying} busyLabel="Checking...">
          Check Status
        </Button>
        <Button variant="danger" icon={<Trash2 size={16} />} onClick={() => setConfirmRemove(true)} busy={isRemoving} busyLabel="Removing...">
          Remove Domain
        </Button>
      </Inline>

      <ConfirmDialog
        open={confirmRemove}
        title="Remove this email domain?"
        description="You will need to set it up again."
        confirmLabel="Remove Domain"
        busy={isRemoving}
        onCancel={() => setConfirmRemove(false)}
        onConfirm={async () => {
          await handleRemove()
          setConfirmRemove(false)
        }}
      />
    </Stack>
  )
}

// Main component
const EmailDomainSetup = () => {
  const { currentWorkspace, isLoading: workspaceLoading } = useWorkspaceStore()
  const [domainConfig, setDomainConfig] = useState<EmailDomainConfig | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const loadDomainConfig = async () => {
    if (!currentWorkspace) return

    setIsLoading(true)
    setError(null)

    try {
      const response = await emailDomainApi.get(currentWorkspace.id)
      if (response.success && response.data) {
        setDomainConfig(response.data)
      } else if (response.error?.includes('404') || response.error?.includes('No domain')) {
        setDomainConfig(null)
      } else {
        setError(response.error || 'Failed to load domain configuration')
      }
    } catch (err) {
      // 404 means no domain configured - that's OK
      if (err instanceof Error && err.message.includes('404')) {
        setDomainConfig(null)
      } else {
        setError(err instanceof Error ? err.message : 'Failed to load domain configuration')
      }
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    loadDomainConfig()
    // Reload only when the workspace changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentWorkspace?.id])

  if (workspaceLoading || isLoading) {
    return <LoadingState caption="Loading..." />
  }

  if (error) {
    return (
      <Alert
        tone="danger"
        live="polite"
        actions={<Button variant="ghost" size="sm" onClick={loadDomainConfig}>Retry</Button>}
      >
        {error}
      </Alert>
    )
  }

  if (!domainConfig) {
    return <DomainSetupForm onSuccess={loadDomainConfig} />
  }

  return (
    <DomainStatusView
      config={domainConfig}
      onRefresh={loadDomainConfig}
      onRemove={() => setDomainConfig(null)}
    />
  )
}

export default EmailDomainSetup
